import { m } from "@estate-platform/i18n/messages";
import { RegionLabelProvider } from "@estate-platform/ui/region";
import { createRootRouteWithContext, lazyRouteComponent, Outlet } from "@tanstack/react-router";
import { lazy, Suspense } from "react";

import { StepUpProvider } from "../auth/step-up.tsx";
import { ErrorPage } from "../shell/error-page.tsx";
import { ToasterProvider } from "../shell/toaster.tsx";

import type { RouterContext } from "../router.tsx";
import type { ReactElement } from "react";

// WHY: Workbox loads after first paint; its update toast goes through the root toaster.
const ServiceWorkerUpdates = lazy(async () => {
  const { ServiceWorkerUpdates: component } = await import("../shell/service-worker-updates.tsx");
  return { default: component };
});

const Root = (): ReactElement => (
  <ToasterProvider>
    <StepUpProvider>
      <RegionLabelProvider label={m.loading()}>
        <Outlet />
      </RegionLabelProvider>
    </StepUpProvider>
    {import.meta.env.PROD && "serviceWorker" in navigator && (
      // eslint-disable-next-line local/no-null-suspense-fallback -- Workbox registers in the background and draws nothing until an update raises a toast
      <Suspense fallback={null}>
        <ServiceWorkerUpdates />
      </Suspense>
    )}
  </ToasterProvider>
);

// WHY: Not found loads on first use, keeping Base UI out of the first load; the error page ships in the entry, since a failed chunk is one of the errors it shows.
export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: lazyRouteComponent(async () => import("../shell/status-pages.tsx"), "NotFoundPage"),
  errorComponent: ErrorPage,
});
